import { SpaceLabel } from '../../../types/TaskLabel';
import LabelChip, { LabelChipType } from '../../UI/LabelChip';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HolderOutlined } from '@ant-design/icons';

export const LabelsGroup: React.FC<{
  labels: SpaceLabel[];
  selectedLabel: SpaceLabel | null;
  selectLabel: (label: SpaceLabel) => void;
  updateLabels: (labels: SpaceLabel[]) => void;
}> = ({ labels, selectedLabel, selectLabel, updateLabels }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  function onDragStart(event: DragStartEvent) {}

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    const indexWhereDropped = labels.findIndex((l) => l.id === +overId);
    const indexFrom = labels.findIndex((l) => l.id === +activeId);

    const orderArray = arrayMove(labels, indexFrom, indexWhereDropped);

    const newLabels = orderArray.map((label, index) => {
      return {
        ...label,
        orderIndex: index,
      };
    });

    updateLabels(newLabels);
  }

  function onDragOver(event: DragOverEvent) {}

  const sortedLabels = labels.sort((a, b) => (a.orderIndex ?? a.id) - (b.orderIndex ?? b.id));

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragOver={onDragOver}>
      <SortableContext items={labels.map((label) => label.id)}>
        <div className="space-y-1">
          {sortedLabels.map((label: SpaceLabel) => (
            <LabelSortable
              key={label.id}
              label={label}
              isSelected={selectedLabel?.id === label.id}
              selectLabel={selectLabel}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export const LabelSortable: React.FC<{
  label: SpaceLabel;
  isSelected: boolean;
  selectLabel: (label: SpaceLabel) => void;
}> = ({ label, isSelected, selectLabel }) => {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: label.id,
    data: {
      type: 'SpaceLabel',
      label,
    },
  });

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`flex items-center px-2 py-1 rounded cursor-pointer hover:bg-neutral-100 ${
        isSelected ? 'bg-neutral-100 border border-solid border-neutral-300' : 'border border-solid border-transparent'
      }`}
      onClick={() => selectLabel(label)}
      label-title-id={label.title}
    >
      <HolderOutlined className="mr-2 text-gray-400" />
      <LabelChip label={label} type={LabelChipType.Modal} />
    </div>
  );
};
